import { DateTime } from "luxon";
import { FC, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { Color, Icon, Panel, Tag } from "capitalrx-components-2.0/lib/general";
import { Grid, Shelf, Stack, useResponsive } from "capitalrx-components-2.0/lib/layout";
import { Body, Semibold, Subheading, TextColor } from "capitalrx-components-2.0/lib/typography";

import { MemberClaim } from "@/App/api/member";
import { ThemedButton } from "@/App/components";

import { getFormattedValue } from "capitalrx-components-2.0/lib/general/Table/helpers";

import "./ClaimsPage.scss";

export interface ClaimListItemProps {
  readonly pharmacy_claim: MemberClaim;
  readonly onClaimView: (pharmacy_claim: MemberClaim) => void;
}

export const PharmacyClaimListItem: FC<ClaimListItemProps> = ({ pharmacy_claim, onClaimView }) => {
  const { t: translate } = useTranslation();
  const { splitColumns } = useResponsive({
    splitColumns: {
      mobile: [6, 6],
      tablet: [6, 6],
      desktop: [8, 4],
    },
  });
  const {
    date_of_service,
    patient_pay_amount,
    pharmacy_name,
    product_service_description,
    savings_opportunity,
    id,
    coverage_strategy_type,
    direct_member_reimbursement_indicator,
  } = pharmacy_claim;

  const formattedDate = useMemo(() => {
    return DateTime.fromISO(pharmacy_claim?.date_of_service, { zone: "utc" }).toFormat("DDD");
  }, [date_of_service]);

  const isDirectMemberReimbursement = (direct_member_reimbursement_indicator ?? "0") !== "0";

  return (
    <Panel className="claims-panel" appearance="primary">
      <Stack>
        <Grid spacing="mini">
          <Grid.Span columns={splitColumns[0]}>
            <Stack spacing="none">
              <Semibold><TextColor appearance={Color.DarkGrey["400"]}>{formattedDate}</TextColor></Semibold>
              <Subheading style={{ wordBreak: "break-all" }}>{product_service_description}</Subheading>
              {coverage_strategy_type === "medicare_prescription_payment_plan" && (
                <Body>
                  {translate("CLAIMS.claimListItem.mpppClaim")}
                </Body>
              )}
            </Stack>
          </Grid.Span>
          <Grid.Span columns={splitColumns[1]}>
            <Stack spacing="mini" alignment="right">
              <Stack spacing="none" alignment="right">
                <Body textAlign="right">
                  <Semibold>
                    <TextColor appearance={Color.DarkGrey["400"]}>{translate("CLAIMS.claimListItem.patientResponsibility")}</TextColor>
                  </Semibold>
                </Body>
                <Semibold><TextColor appearance={Color.DarkGrey["500"]}>${getFormattedValue(patient_pay_amount)}</TextColor></Semibold>
              </Stack>
              {savings_opportunity && (
                <Tag testId={`claim-${id}-savings_opportunity`} type="success" style={{ paddingTop: "1px", paddingBottom: "1px" }}>
                  <Shelf spacing="small" alignment="center">
                    <Icon size="small" color={Color.Green[700]} name="dollar-circle" />
                    {translate("CLAIMS.claimListItem.savingsOpportunity")}
                  </Shelf>
                </Tag>
              )}
            </Stack>
          </Grid.Span>
        </Grid>
        <Stack spacing="mini">
          <Semibold><TextColor appearance={Color.DarkGrey["400"]}>{translate("CLAIMS.claimListItem.pharmacy")}</TextColor></Semibold>
          <Body>{pharmacy_name}</Body>
        </Stack>
        <Shelf alignment="bottom">
          <ThemedButton icon="eye" testId={`view-claim-${id}-button`} onClick={() => onClaimView(pharmacy_claim)} appearance="secondary">
            {translate("CLAIMS.claimListItem.view")}
          </ThemedButton>
          <Shelf.Spacer />
          {
            isDirectMemberReimbursement && (
              <Body textAlign="right">
                <Semibold>
                  <TextColor appearance={Color.DarkGrey["400"]}>{translate("CLAIMS.claimListItem.reimbursementClaim")}</TextColor>
                </Semibold>
              </Body>
            )
          }
        </Shelf>
      </Stack>
    </Panel>
  );
};
